/**
 * Identity page — Module 2 (digital identity), in depth.
 *
 *  - Bios: 3 strategic variants per platform — the user picks their favourite.
 *  - Charter: 3 colour palettes, selectable; drives the capture-page preview.
 *  - Capture page: a real, live preview that applies the chosen palette + copy.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Sparkles,
  Instagram,
  Check,
  RefreshCw,
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  useIdentity,
  type BrandKit,
  type BioVariant,
  type SectionType,
} from '@/hooks/useIdentity';
import { CapturePageBuilder } from '@/components/CapturePageBuilder';
import { ContentToRegenerate } from '@/components/ContentToRegenerate';
export default function IdentityPage() {
  const { t } = useTranslation();
  const {
    kit,
    isLoading,
    generate,
    update,
    regenerateLanding,
    regenerateLandingSection,
  } = useIdentity();
  const navigate = useNavigate();
  const [regeneratingSection, setRegeneratingSection] = useState<SectionType | null>(null);
  // Shared palette selection — drives both the charter and the capture preview.
  const [paletteIndex, setPaletteIndex] = useState(0);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-pink" size={30} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[880px] px-5 py-10 sm:px-8">
      <header className="mb-8 flex items-center gap-3.5">
        <div className="flex-1">
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">{t('identity.kicker')}</p>
          <h1 className="font-display text-[clamp(22px,3vw,30px)] font-800 tracking-[-0.025em]">{t('identity.title')}</h1>
        </div>
        {kit && (
          <button
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            title={t('identity.regenerateTitle')}
            className="btn-ghost px-4 py-2.5 text-[13px] disabled:opacity-60"
          >
            {generate.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            {t('identity.regenerate')}
          </button>
        )}
      </header>

      {!kit ? (
        <EmptyState
          generating={generate.isPending}
          error={generate.error?.message ?? null}
          onGenerate={() => generate.mutate()}
        />
      ) : kit.stale ? (
        <ContentToRegenerate
          stored={kit.lang ?? 'fr'}
          onRegenerate={() => generate.mutate()}
          busy={generate.isPending}
        />
      ) : (
        <div className="space-y-5">
          {/* 12/10/2026 — l'entretien a été refait après cette génération :
              on le dit, on ne régénère jamais d'office. */}
          {kit.outdated && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-hi rounded-2xl p-5"
            >
              <p className="font-display text-[15px] font-700 text-ink">{t('identity.outdatedTitle')}</p>
              <p className="mt-1.5 text-[13px] leading-[1.55] text-ink/70">{t('identity.outdatedBody')}</p>
              <button
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
                className="btn-primary mt-3.5 px-4 py-2 text-[12.5px] disabled:opacity-60"
              >
                {generate.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                {t('identity.outdatedCta')}
              </button>
            </motion.div>
          )}
          <p className="text-[12.5px] leading-[1.55] text-ink/55">
            {t('identity.basicsHint')}{' '}
            <Link to="/profil" className="font-600 text-pink underline-offset-4 hover:underline">
              {t('identity.basicsLink')}
            </Link>
            .
          </p>
          <BioSection
            kit={kit}
            onSave={(bios) => update.mutate({ bios })}
            saving={update.isPending}
          />
          <PaletteSection kit={kit} selected={paletteIndex} onSelect={setPaletteIndex} />
          <SectionCard kicker="Page de capture" title={t("identity.captureSectionTitle")} delay={0.15}>
            <CapturePageBuilder
              kit={kit}
              paletteIndex={paletteIndex}
              onSave={(capture_page) => update.mutate({ capture_page })}
              saving={update.isPending}
              onRegenerateAll={() => regenerateLanding.mutate()}
              onRegenerateSection={(type) => {
                setRegeneratingSection(type);
                regenerateLandingSection.mutate(type, {
                  onSettled: () => setRegeneratingSection(null),
                });
              }}
              regeneratingAll={regenerateLanding.isPending}
              regeneratingSection={regeneratingSection}
            />
          </SectionCard>

          <div className="flex flex-wrap gap-3 pt-2">
            <button onClick={() => navigate('/studio')} className="btn-primary">
              {t('identity.createVisuals')}
            </button>
            <button onClick={() => navigate('/')} className="btn-ghost">
              {t('common.backToSpace')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({
  generating,
  error,
  onGenerate,
}: {
  generating: boolean;
  error: string | null;
  onGenerate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-hi rounded-3xl p-9 text-center"
    >
      <span
        className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white"
        style={{ background: 'linear-gradient(135deg, #3b82f6, #ec4899)' }}
      >
        <Sparkles size={24} />
      </span>
      <h2 className="font-display text-xl font-800 tracking-[-0.02em]">
        {t('identity.emptyTitle')}
      </h2>
      <p className="mx-auto mt-2.5 max-w-md text-[14px] leading-[1.6] text-ink/65">
        {t('identity.emptyBody')}
      </p>
      {error && (
        <p className="mx-auto mt-4 max-w-md rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-500 text-pink-deep">
          {error}
        </p>
      )}
      <button
        onClick={onGenerate}
        disabled={generating}
        className="btn-primary mt-6 disabled:opacity-60"
      >
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {generating ? t('identity.generating') : t('identity.generate')}
      </button>
    </motion.div>
  );
}

function SectionCard({
  kicker,
  title,
  children,
  delay = 0,
}: {
  kicker: string;
  title: string;
  children: React.ReactNode;
  delay?: number;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="card p-6 sm:p-7"
    >
      <p className="font-mono text-[10px] font-700 uppercase tracking-[0.16em] text-ink/45">
        {kicker}
      </p>
      <h3 className="mb-4 mt-1 font-display text-[18px] font-700">{title}</h3>
      {children}
    </motion.section>
  );
}

// ── Bios — 3 selectable variants per platform ────────────────────────────────

function BioSection({
  kit,
  onSave,
  saving,
}: {
  kit: BrandKit;
  onSave: (bios: BrandKit['bios']) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  // The "active" variant per platform is simply index 0 — the user promotes a
  // variant to the top by selecting it, which is what gets saved as primary.
  const [igOrder, setIgOrder] = useState(kit.bios.instagram);
  const [ttOrder, setTtOrder] = useState(kit.bios.tiktok);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setIgOrder(kit.bios.instagram);
    setTtOrder(kit.bios.tiktok);
    setDirty(false);
  }, [kit.bios]);

  function promote(platform: 'ig' | 'tt', index: number): void {
    const list = platform === 'ig' ? [...igOrder] : [...ttOrder];
    const [picked] = list.splice(index, 1);
    if (!picked) return;
    list.unshift(picked);
    if (platform === 'ig') setIgOrder(list);
    else setTtOrder(list);
    setDirty(true);
  }

  return (
    <SectionCard kicker={t('identity.bioSection')} title={t("identity.bioSectionTitle")} delay={0.05}>
      <p className="mb-4 text-[13px] leading-[1.55] text-ink/60">
        {t('identity.bioSectionIntro')}
      </p>

      <BioPlatform
        icon={<Instagram size={15} />}
        label="Instagram"
        max={150}
        variants={igOrder}
        onSelect={(i) => promote('ig', i)}
      />
      <div className="mt-5">
        <BioPlatform
          icon={<span className="text-[12px] font-800">TT</span>}
          label="TikTok"
          max={80}
          variants={ttOrder}
          onSelect={(i) => promote('tt', i)}
        />
      </div>

      {dirty && (
        <button
          onClick={() => {
            onSave({ instagram: igOrder, tiktok: ttOrder });
            setDirty(false);
          }}
          disabled={saving}
          className="btn-primary mt-5 px-4 py-2 text-[12.5px] disabled:opacity-60"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          Enregistrer mon choix
        </button>
      )}
    </SectionCard>
  );
}

function BioPlatform({
  icon,
  label,
  max,
  variants,
  onSelect,
}: {
  icon: React.ReactNode;
  label: string;
  max: number;
  variants: BioVariant[];
  onSelect: (index: number) => void;
}) {
  return (
    <div>
      <span className="mb-2 flex items-center gap-1.5 text-[12px] font-700 text-ink/70">
        {icon}
        {label}
      </span>
      <div className="space-y-2">
        {variants.map((v, i) => {
          const selected = i === 0;
          const over = v.text.length > max;
          return (
            <button
              key={`${label}-${i}`}
              onClick={() => onSelect(i)}
              className={`block w-full rounded-2xl border p-3.5 text-left transition ${
                selected
                  ? 'border-pink/50 bg-pink/[0.04] shadow-[0_4px_14px_-8px_rgba(236,72,153,0.5)]'
                  : 'border-ink/[0.09] bg-white hover:border-pink/30'
              }`}
            >
              <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                      selected ? 'border-pink bg-pink text-white' : 'border-ink/25'
                    }`}
                  >
                    {selected && <Check size={10} />}
                  </span>
                  <span className="font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-ink/50">
                    {v.angle}
                  </span>
                  {selected && (
                    <span className="rounded-full bg-pink/15 px-1.5 py-0.5 text-[9px] font-700 uppercase tracking-[0.1em] text-pink">
                      Active
                    </span>
                  )}
                </span>
                <span className={`font-mono text-[10.5px] ${over ? 'text-pink-deep' : 'text-ink/40'}`}>
                  {v.text.length}/{max}
                </span>
              </div>
              <p className="text-[13.5px] leading-[1.5] text-ink/85">{v.text}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Palettes — selectable, drives the capture-page preview ───────────────────

function PaletteSection({
  kit,
  selected,
  onSelect,
}: {
  kit: BrandKit;
  selected: number;
  onSelect: (i: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <SectionCard kicker={t('identity.paletteSection')} title={t("identity.paletteSectionTitle")} delay={0.1}>
      <p className="mb-4 text-[13px] leading-[1.55] text-ink/60">
        {t('identity.paletteIntro')}
      </p>
      <div className="grid gap-4 sm:grid-cols-3">
        {kit.palettes.map((p, i) => {
          const active = i === selected;
          return (
            <button
              key={i}
              onClick={() => onSelect(i)}
              className={`rounded-2xl border p-3 text-left transition ${
                active ? 'border-pink/50 bg-pink/[0.03]' : 'border-ink/[0.07] hover:border-pink/30'
              }`}
            >
              <div className="mb-2 flex h-16 overflow-hidden rounded-xl">
                {p.colors.map((c, j) => (
                  <div key={j} className="flex-1" style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center justify-between">
                <p className="font-display text-[13px] font-700 text-ink">{p.name}</p>
                {active && <Check size={13} className="text-pink" />}
              </div>
              <p className="mt-0.5 font-mono text-[9.5px] text-ink/40">{p.colors.join(' · ')}</p>
            </button>
          );
        })}
      </div>
      <p className="mt-4 text-[12.5px] text-ink/55">
        Typographies suggérées :{' '}
        <span className="font-600 text-ink/75">{kit.typos.title}</span> (titres) ·{' '}
        <span className="font-600 text-ink/75">{kit.typos.body}</span> (texte)
      </p>
    </SectionCard>
  );
}
